<script setup lang="ts">
import { ref, watch } from 'vue'

import TazaCafe from '@/components/TazaCafe.vue'
import { useBienvenida } from '@/composables/useBienvenida'

/**
 * Bienvenida de un segundo tras iniciar sesión: la taza se llena de golpe, el
 * saludo sube y se desvanece sobre la pantalla de destino. Montada una sola
 * vez en App.vue; la dispara el login con useBienvenida().
 */
const { estado } = useBienvenida()

const progreso = ref(0)
watch(
  () => estado.visible,
  (v) => {
    if (!v) return
    progreso.value = 0
    // Dos cuadros: primero se pinta la taza vacía, luego se llena con transición.
    requestAnimationFrame(() => requestAnimationFrame(() => (progreso.value = 1)))
  },
)
</script>

<template>
  <Transition name="bienvenida">
    <div
      v-if="estado.visible"
      class="dark fixed inset-0 z-[100] grid place-items-center px-6"
      style="background: radial-gradient(120% 120% at 50% 0%, #2a1d12 0%, #15100a 55%, #0d0a06 100%)"
      role="status"
      aria-live="polite"
    >
      <div class="contenido flex flex-col items-center text-center">
        <div class="taza relative isolate">
          <div class="halo absolute -inset-8 -z-10 rounded-full" />
          <TazaCafe :progreso="progreso" :duracion="0.65" class="h-32 w-32 sm:h-36 sm:w-36" />
        </div>
        <p class="linea mt-4 text-sm font-medium uppercase tracking-[0.2em] text-[#f5ece0]/60" style="animation-delay: 120ms">
          {{ estado.saludo }},
        </p>
        <p class="linea mt-1 text-3xl font-bold tracking-tight text-[#c8a97e] sm:text-4xl" style="animation-delay: 220ms">
          {{ estado.nombre }}
        </p>
        <p class="linea mt-3 text-xs text-[#f5ece0]/50" style="animation-delay: 380ms">
          {{ estado.subtitulo }}
        </p>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.halo {
  background: radial-gradient(circle, rgb(200 169 126 / 0.28), transparent 65%);
  animation: halo 1.6s ease-in-out infinite;
}
.taza {
  animation: taza 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}
.linea {
  animation: subir 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}

.bienvenida-enter-active {
  transition: opacity 0.2s ease;
}
.bienvenida-leave-active {
  transition: opacity 0.35s ease;
}
.bienvenida-leave-active .contenido {
  transition: transform 0.35s ease;
}
.bienvenida-enter-from,
.bienvenida-leave-to {
  opacity: 0;
}
.bienvenida-leave-to .contenido {
  transform: scale(1.06);
}

@keyframes taza {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(8px);
  }
}
@keyframes subir {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}
@keyframes halo {
  0%,
  100% {
    transform: scale(0.94);
  }
  50% {
    transform: scale(1.06);
  }
}

@media (prefers-reduced-motion: reduce) {
  .halo,
  .taza,
  .linea {
    animation: none;
  }
  .bienvenida-leave-to .contenido {
    transform: none;
  }
}
</style>
